import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthShell } from './components/AuthShell';
import { brand } from '@/config/brand';
import { MigrationOtpForm } from './components/MigrationOtpForm';
import { MigrationSetPasswordForm } from './components/MigrationSetPasswordForm';

type Step =
  | { kind: 'otp' }
  | { kind: 'password'; migrationToken: string };

/**
 * SSO auto-migration page. Reached from the login screen once an email is found
 * to be migrationable (an OTP has already been sent). Walks the user through
 * OTP → set password → SSO sign-in. The email arrives via router state; without
 * it there is nothing to migrate, so we bounce back to login.
 */
export default function MigrationPage() {
  const location = useLocation();
  const state = location.state as { email?: string; from?: string } | null;
  const email = state?.email;
  // Where to land after the account is set up (carried from the login redirect).
  const from = state?.from || '/';
  const [step, setStep] = useState<Step>({ kind: 'otp' });

  if (!email) {
    return <Navigate to="/auth/login" replace />;
  }

  if (step.kind === 'password') {
    return (
      <AuthShell
        title="Set your password"
        subtitle={`Create a password for your new ${brand.ssoName} sign-in.`}
      >
        <MigrationSetPasswordForm
          email={email}
          migrationToken={step.migrationToken}
          from={from}
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Verify your migration"
      subtitle={`We're upgrading your account to ${brand.ssoName} single sign-on.`}
    >
      <MigrationOtpForm
        email={email}
        onVerified={(migrationToken) => setStep({ kind: 'password', migrationToken })}
      />
    </AuthShell>
  );
}
